import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SectionHero } from '../components/sections/SectionHero';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { ScrollArea } from '../components/ui/scroll-area';
import { ChevronRight, Calendar, Users, UserCheck, Loader2, Trophy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Format date for display
const formatDate = (dateStr, language) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const locale = language === 'ca' ? 'ca-ES' : language === 'es' ? 'es-ES' : language === 'fr' ? 'fr-FR' : 'en-GB';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const TournamentCard = ({ tournament, isPast, language }) => {
  const hasLinks = tournament.link_members || tournament.link_visitors;
  
  return (
    <motion.div
      className="group bg-white border border-[#E5E5E5] overflow-hidden hover:border-[#1A4D2E] transition-colors duration-300"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid={`tournament-${tournament.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        {tournament.image ? (
          <img 
            src={tournament.image} 
            alt={tournament.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1A4D2E] to-[#2a6d4e] flex items-center justify-center">
            <Trophy className="w-12 h-12 text-white/30" />
          </div>
        )}
        {isPast && (
          <div className="absolute inset-0 bg-black/30" />
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-[#D4AF37] uppercase tracking-wider mb-1 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatDate(tournament.date, language)}
        </p>
        <h3 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#1A4D2E] transition-colors duration-200 mb-2">
          {tournament.name}
        </h3>
        
        {/* Description */}
        {tournament.description && (
          <p className="text-xs text-[#666666] line-clamp-2 mb-3">
            {tournament.description}
          </p>
        )}
        
        {/* Links */}
        {hasLinks && (
          <div className="flex gap-2 pt-2 border-t border-[#E5E5E5]">
            {tournament.link_members && (
              <a
                href={tournament.link_members}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-[#1A4D2E] hover:text-[#D4AF37] transition-colors font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                <UserCheck className="w-3 h-3" />
                {language === 'ca' ? 'Membres' : language === 'es' ? 'Miembros' : language === 'fr' ? 'Membres' : 'Members'}
              </a>
            )}
            {tournament.link_visitors && (
              <a
                href={tournament.link_visitors}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-[#1A4D2E] hover:text-[#D4AF37] transition-colors font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                <Users className="w-3 h-3" />
                {language === 'ca' ? 'Visitants' : language === 'es' ? 'Visitantes' : language === 'fr' ? 'Visiteurs' : 'Visitors'}
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const CompeticioPage = () => {
  const { t, language } = useLanguage();
  const [tournaments, setTournaments] = useState({ future: [], past: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/tournaments`);
        if (response.ok) {
          const data = await response.json();
          setTournaments({
            future: data.future || [],
            past: data.past || []
          });
        }
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, []);

  const translations = {
    ca: {
      passats: 'Torneigs Passats',
      futurs: 'Pròxims Torneigs',
      calendari: 'Calendari',
      participar: 'Vols participar?',
      participarDesc: "Inscriu-te als nostres torneigs i competicions. Contacta amb la secretaria per més informació.",
      noFuture: 'No hi ha torneigs programats properament',
      noPast: 'No hi ha torneigs passats registrats'
    },
    es: {
      passats: 'Torneos Pasados',
      futurs: 'Próximos Torneos',
      calendari: 'Calendario',
      participar: '¿Quieres participar?',
      participarDesc: 'Inscríbete a nuestros torneos y competiciones. Contacta con la secretaría para más información.',
      noFuture: 'No hay torneos programados próximamente',
      noPast: 'No hay torneos pasados registrados'
    },
    fr: {
      passats: 'Tournois Passés',
      futurs: 'Prochains Tournois',
      calendari: 'Calendrier',
      participar: 'Voulez-vous participer?',
      participarDesc: "Inscrivez-vous à nos tournois et compétitions. Contactez le secrétariat pour plus d'informations.",
      noFuture: 'Aucun tournoi prévu prochainement',
      noPast: 'Aucun tournoi passé enregistré'
    },
    en: {
      passats: 'Past Tournaments',
      futurs: 'Upcoming Tournaments',
      calendari: 'Calendar',
      participar: 'Want to participate?',
      participarDesc: 'Sign up for our tournaments and competitions. Contact the secretariat for more information.',
      noFuture: 'No upcoming tournaments scheduled',
      noPast: 'No past tournaments recorded'
    }
  };

  const txt = translations[language] || translations.ca;

  return (
    <main data-testid="competicio-page">
      <SEO title={PAGE_SEO.competicio.title} description={PAGE_SEO.competicio.description} />
      <SectionHero
        image="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/gfa1pb3x_ARAVELL_HERO_COMPETICIO_1920x700.jpeg"
        title={t('competicio.title')}
        subtitle={t('competicio.subtitle')}
      />

      {/* Competition Layout */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="competition-layout">
            {/* Past Tournaments Column */}
            <div className="space-y-4 lg:order-3" data-testid="past-tournaments">
              <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E] mb-4">
                {txt.passats}
              </h3>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1A4D2E]" />
                </div>
              ) : tournaments.past.length > 0 ? (
                <ScrollArea className="h-[500px] lg:h-[600px] pr-4">
                  <div className="space-y-4">
                    {tournaments.past.map((tournament) => (
                      <TournamentCard 
                        key={tournament.id} 
                        tournament={tournament} 
                        isPast={true} 
                        language={language}
                      />
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-6 text-center">
                  <Trophy className="w-8 h-8 text-[#CCCCCC] mx-auto mb-2" />
                  <p className="text-sm text-[#888888]">{txt.noPast}</p>
                </div>
              )}
            </div>

            {/* Calendar Column */}
            <div className="order-first lg:order-2" data-testid="calendar-section">
              <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E] mb-4">
                {txt.calendari}
              </h3>
              <div className="bg-[#F9F9F7] border border-[#E5E5E5] p-2 md:p-4 h-[450px] md:h-[500px] lg:h-[600px]">
                {/* Mobile: AGENDA view */}
                <iframe
                  src={`https://calendar.google.com/calendar/embed?src=d1206b85ae4642f9afee874cf05a0278b854273e1aa5d96162648537dce537ad%40group.calendar.google.com&ctz=Europe%2FMadrid&showTitle=0&showNav=1&showDate=1&showPrint=0&showTabs=0&showCalendars=0&showTz=0&mode=AGENDA&wkst=2&hl=${language}`}
                  style={{ border: 0 }}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="yes"
                  title="UGOLF ARAVELL Andorra Calendar"
                  className="block md:hidden"
                />
                {/* Desktop: MONTH view */}
                <iframe
                  src={`https://calendar.google.com/calendar/embed?src=d1206b85ae4642f9afee874cf05a0278b854273e1aa5d96162648537dce537ad%40group.calendar.google.com&ctz=Europe%2FMadrid&showTitle=0&showNav=1&showDate=1&showPrint=0&showTabs=0&showCalendars=0&showTz=0&mode=MONTH&wkst=2&hl=${language}`}
                  style={{ border: 0 }}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  title="UGOLF ARAVELL Andorra Calendar"
                  className="hidden md:block"
                />
              </div>
              {/* Fallback button for users who can't see the iframe */}
              <a
                href="https://calendar.google.com/calendar/u/0?cid=ZDEyMDZiODVhZTQ2NDJmOWFmZWU4NzRjZjA1YTAyNzhiODU0MjczZTFhYTVkOTYxNjI2NDg1MzdkY2U1MzdhZEBncm91cC5jYWxlbmRhci5nb29nbGUuY29t"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-white border border-[#E5E5E5] text-[#1A4D2E] text-xs font-medium tracking-wide uppercase hover:bg-[#1A4D2E] hover:text-white hover:border-[#1A4D2E] transition-colors duration-200"
                data-testid="open-calendar-btn"
              >
                <Calendar className="w-4 h-4" />
                {language === 'ca' ? 'Obre calendari complet' : 
                 language === 'es' ? 'Abrir calendario completo' : 
                 language === 'fr' ? 'Ouvrir calendrier complet' : 
                 'Open full calendar'}
              </a>
            </div>

            {/* Future Tournaments Column */}
            <div className="space-y-4 lg:order-1" data-testid="future-tournaments">
              <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E] mb-4">
                {txt.futurs}
              </h3>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1A4D2E]" />
                </div>
              ) : tournaments.future.length > 0 ? (
                <ScrollArea className="h-[500px] lg:h-[600px] pr-4">
                  <div className="space-y-4">
                    {tournaments.future.map((tournament) => (
                      <TournamentCard 
                        key={tournament.id} 
                        tournament={tournament} 
                        isPast={false}
                        language={language}
                      />
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-6 text-center">
                  <Calendar className="w-8 h-8 text-[#CCCCCC] mx-auto mb-2" />
                  <p className="text-sm text-[#888888]">{txt.noFuture}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-2xl text-[#1A1A1A] font-medium mb-4">
              {txt.participar}
            </h2>
            <p className="text-[#666666] mb-6 max-w-md mx-auto">
              {txt.participarDesc}
            </p>
            <a
              href="mailto:secretaria@aravellgolfclub.com"
              className="inline-block bg-[#1A4D2E] text-white px-8 py-4 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200"
              data-testid="competicio-contact-btn"
            >
              {t('nav.contacte')}
            </a>
          </motion.div>

          {/* Data Protection Notice */}
          <div className="max-w-4xl mx-auto mt-12 text-left text-xs text-[#777777] italic leading-relaxed space-y-3 border-t border-[#E5E5E5] pt-8" data-testid="competicio-gdpr-notice">
            <p>
              <strong>PROTECCIÓN DE DATOS:</strong> La inscripción al torneo supone la aceptación de estas bases y el tratamiento de sus datos personales por parte de UGOLF IBERIA, S.L. como entidad gestora/operadora ARAVELL GOLF, y por ARAVELL GOLF CLUB S.L.U., como entidad titular del campo, para gestionar la inscripción, mantener el contacto y la comunicación con los jugadores, (por ejemplo para enviarles SMS con los horarios de salida, gestionar las clasificaciones, etc) y, en su caso, remitirle información sobre actividades, productos y/o servicios de nuestro campo. Dicho tratamiento se basa en la relación contractual existente entre las partes, en nuestro interés legítimo y en su consentimiento. Comunicamos datos a terceros (como a la Federaciones nacional y autonómicas de golf para la gestión de resultados y handicaps).
            </p>
            <p>
              En los torneos que organicemos se podrán hacer fotografías y/o videos que podrán ser publicados, tanto en internet (en nuestros perfiles en cualesquiera redes sociales), como en papel (en carteles publicitarios o informativos, folletos, programas, revistas, memorias y demás papelería o soportes que pueda utilizar la organización para informar sobre sus actividades o promocionarlas), para informar sobre el torneo y documentarlo.
            </p>
            <p>
              Puede, cuando proceda, acceder, rectificar, suprimir, oponerse, pedir la portabilidad de los datos, limitar el tratamiento, y no ser objeto de decisiones sólo automatizadas, como se detalla en la información adicional y completa que puede ver en la{' '}
              <a href="/legal/privacitat" className="text-[#1A4D2E] underline hover:no-underline not-italic">política de privacidad</a>
              {' '}de la página web.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CompeticioPage;
